import { BehaviorSubject } from 'rxjs';
import {
    Component,
    ElementRef,
    EventEmitter,
    HostListener,
    Input,
    OnInit,
    Output,
    QueryList,
    ViewChildren,
    ViewEncapsulation,
    ChangeDetectionStrategy,
} from '@angular/core';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { isDefined } from '@fi-shared/utils';
import { CheckboxModel } from './checkbox.model';

@Component({
    selector: 'fi-checkbox',
    templateUrl: './checkbox.component.html',
    encapsulation: ViewEncapsulation.None,
    styleUrls: ['./checkbox.component.scss'],
    exportAs: 'fiCheckbox',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class CheckboxComponent implements OnInit {
    @Input() checkboxSettings: CheckboxModel;
    @Input() multiSelect: boolean = true;
    @Input() disabled: boolean = false;
    @Input() title;
    @Input() disableIds: Array<number>;

    @Output('checkedChange') checkedChange: EventEmitter<any> = new EventEmitter<any>();
    @Output('indeterminateChange') indeterminateChangeEmitter: EventEmitter<any> = new EventEmitter<any>();

    @ViewChildren('childCheckbox') childrenCheckboxes: QueryList<CheckboxComponent>;

    ChildItems: Array<any> = [];
    ShowChildren: boolean = false;
    checked$ = new BehaviorSubject<boolean>(false);
    indeterminate: boolean = null;

    constructor(private elementRef: ElementRef) {}

    ngOnInit() {
        if (!isDefined(this.checkboxSettings.IsEnabled)) this.checkboxSettings.IsEnabled = true;

        if (!isDefined(this.checkboxSettings.Checked)) {
            const childItems = this.getChildItems(this.checkboxSettings);
            this._setCheckedStateInternal(childItems && childItems.length && childItems.every((item) => item.Checked));
        }
        this.checked$.next(this.checkboxSettings.Checked);
        this.updateChildItems();
        setTimeout(() => this.updateIndeterminateState());
    }

    @HostListener('click', ['$event'])
    onClick(event: MouseEvent): void {
        if (this.checkboxSettings.OnBeforeClick) {
            event.preventDefault();
            if (!this.checkboxSettings.IsEnabled) return;

            this.checkboxSettings.OnBeforeClick(this.checked$.value).then(
                () => {
                    let inputEl = this.elementRef.nativeElement.querySelector('input');
                    if (inputEl && inputEl.click) inputEl.click();
                },
                () => {
                    /* empty catch to prevent Uncaught (in promise) */
                },
            );
        }
    }

    getChildItems(item) {
        return item[this.checkboxSettings.DataChildItemField];
    }

    updateChildItems() {
        this.ChildItems = this.GetAllChildItems(this.getChildItems(this.checkboxSettings));
    }

    CheckboxToggleClicked() {
        this.ShowChildren = !this.ShowChildren;
    }

    HasChildItems() {
        return this.ChildItems.length > 0;
    }

    GetAllChildItems(data) {
        let result = [];

        if (data) {
            data.forEach((item) => {
                if (this.getChildItems(item) && this.getChildItems(item).length > 0) {
                    result.concat(this.GetAllChildItems(this.getChildItems(item)));
                } else {
                    result.push(item);
                }
            });
        }
        return result;
    }

    checkAllChildren(items: Array<any>, checked: boolean) {
        if (items) {
            items.forEach((item) => {
                item.Checked = checked;
                this.checkAllChildren(this.getChildItems(item), checked);
            });
        }
    }

    onCheckedChange($event: MatCheckboxChange) {
        if (this.HasChildItems() && !this.checkboxSettings.IsEnabled) return;

        this.indeterminate = false;

        if (!this.multiSelect) {
            if (this.HasChildItems()) {
                this._setCheckedStateInternal($event.checked);
                return;
            }
        }

        this._setCheckedStateInternal($event.checked);
        this.setChildrenCheckedState();
        this.checkedChange.emit(this.checked$.value);

        //
        // if ($scope.ngBeforeClick) {
        //     $scope.ngBeforeClick($scope.CcmCheckbox.Checked).then(function() {
        //         $scope.CcmCheckbox.Checked = !$scope.CcmCheckbox.Checked;
        //         inputClick();
        //     });
        //     $scope.CcmCheckbox.Checked = !$scope.CcmCheckbox.Checked;
        // } else {
        //     inputClick();
        // }
    }

    setChildrenCheckedState() {
        this.childrenCheckboxes.forEach((checkbox) => {
            checkbox.setCheckedState(this.checked$.value);
        });
    }

    getTitle() {
        // if (this.isDisabled()) {
        //return this.disabledTitle || this.checkboxSettings.Name;
        // }
        return this.title || this.checkboxSettings.Name;
    }

    isDisabled() {
        return this.checkboxSettings.IsEnabled === false || this.disabled === true;
    }

    isChildDisabled(item) {
        return this.disableIds && this.disableIds.indexOf(item.Id) > -1;
    }

    /**
     * Used to set checked state from parent component
     * @param isChecked
     */
    setCheckedState(isChecked) {
        this._setCheckedStateInternal(isChecked);
        this.indeterminate = false;
        this.setChildrenCheckedState();
    }

    setIndeterminateState(indeterminateState) {
        this.indeterminate = indeterminateState;
    }

    protected _setCheckedStateInternal(isChecked) {
        this.checkboxSettings.Checked = isChecked;
        this.checked$.next(isChecked);
    }

    childCheckedChange() {
        const currentCheckedState = this.checked$.value,
            currentIndeterminate = this.indeterminate;
        this._setCheckedStateInternal(this.childrenCheckboxes.some((item) => item.isChecked()));
        this.updateIndeterminateState();
        // emit change when "checked" or "indeterminate" have been changed
        if (this.checked$.value !== currentCheckedState || this.indeterminate !== currentIndeterminate)
            this.checkedChange.emit();
    }

    isChecked() {
        return this.checked$.value;
    }

    isIndeterminate() {
        return this.indeterminate;
    }

    updateIndeterminateState() {
        const currentIndeterminate = this.isIndeterminate();
        // current checkbox considered as indeterminate when some of children are selected,
        // but not all of them or some of them are indeterminate
        this.indeterminate =
            this.childrenCheckboxes.some((item) => item.isChecked()) &&
            !this.childrenCheckboxes.toArray().every((item) => item.isChecked() && !item.isIndeterminate());
        if (this.indeterminate !== currentIndeterminate) this.indeterminateChangeEmitter.emit(this.indeterminate);
    }

    childIndeterminateChange() {
        this.updateIndeterminateState();
    }
}
